import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import {
  DEMO_ACTIVATION_REQUEST_NOTICE,
  api,
  isDemoActivationRequired,
  isLiveActivationRequired,
  isLiveAlreadyExists,
} from './api'
import {
  connectionStatus,
  statusLabel,
  tradingServerLabel,
  type ConnectionPresentation,
} from './branding'
import {
  fixtureAccount,
  fixtureLiveAccount,
  fixtureSummary,
  useFixturesFromQuery,
} from './fixtures'
import type { AccountSummary, TradingAccount } from './types'
import { isSelectableAccount, safeError } from './safe-ui'

export type WorkspaceAccountState = {
  loading: boolean
  refreshing: boolean
  error: string
  notice: string
  account: TradingAccount | null
  summary: AccountSummary | null
  accounts: TradingAccount[]
  openCount: number
  pendingCount: number
  hasAccount: boolean
  hasLiveAccount: boolean
  hasDemoOnly: boolean
  mode: string
  currency: string
  balance: number | string | null | undefined
  equity: number | string | null | undefined
  floating: number
  mt5Login: number | string | null | undefined
  server: string
  status: string
  statusRaw: string
  isAccountActive: boolean
  conn: ConnectionPresentation
  connLabel: string
  lastSync: string | null | undefined
  refresh: () => Promise<void>
  requestDemoAccount: () => Promise<void>
  requestLiveAccount: (
    tradingPassword: string,
    confirmTradingPassword: string,
  ) => Promise<TradingAccount>
  creatingAccount: boolean
  creatingLiveAccount: boolean
  setAccountId: (id: number) => void
  selectedAccountId: number | null
}

const WorkspaceAccountContext = createContext<WorkspaceAccountState | null>(null)

export function WorkspaceAccountProvider({ children }: { children: ReactNode }) {
  const fixtures = useFixturesFromQuery()
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [creatingAccount, setCreatingAccount] = useState(false)
  const [creatingLiveAccount, setCreatingLiveAccount] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [accounts, setAccounts] = useState<TradingAccount[]>([])
  const [account, setAccount] = useState<TradingAccount | null>(null)
  const [summary, setSummary] = useState<AccountSummary | null>(null)
  const [openCount, setOpenCount] = useState(0)
  const [pendingCount, setPendingCount] = useState(0)
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null)
  const selectedAccountIdRef = useRef<number | null>(null)
  selectedAccountIdRef.current = selectedAccountId
  const fixtureLiveCreatedRef = useRef(false)

  const load = useCallback(async () => {
    setError('')
    if (fixtures) {
      if (fixtureLiveCreatedRef.current) {
        const live = fixtureLiveAccount
        setAccounts([fixtureAccount, live])
        setAccount(live)
        setSummary({
          id: live.id,
          mt5_login: live.mt5Login,
          server: live.server,
          mode: live.mode,
          currency: live.currency,
          balance: live.balance,
          equity: live.equity,
          floating_profit: 0,
          status: live.status,
          sync_state: 'LIVE',
          last_sync_at: new Date().toISOString(),
        })
        setSelectedAccountId(live.id)
        setOpenCount(0)
        setPendingCount(0)
        return
      }
      setAccounts([fixtureAccount])
      setAccount(fixtureAccount)
      setSummary(fixtureSummary)
      setSelectedAccountId(fixtureAccount.id)
      setOpenCount(1)
      setPendingCount(1)
      return
    }

    const listed = await api.listAccounts()
    const all = (listed.accounts || []).map((row) => {
      const raw = row as TradingAccount & { mt5_login?: number }
      const mode = String(row.mode || '').toUpperCase()
      return {
        ...row,
        mt5Login: row.mt5Login ?? raw.mt5_login,
        mode: mode || row.mode,
      }
    })
    const selectable = all.filter((a) => isSelectableAccount(a.status))
    setAccounts(all)

    const currentId = selectedAccountIdRef.current
    const livePreferred = selectable.find((a) => {
      const m = String(a.mode || '').toUpperCase()
      return m === 'LIVE' || m === 'REAL'
    })
    const preferred =
      (currentId && selectable.find((a) => a.id === currentId)) ||
      livePreferred ||
      selectable[0] ||
      all[0] ||
      null

    if (!preferred) {
      setAccount(null)
      setSummary(null)
      setSelectedAccountId(null)
      setOpenCount(0)
      setPendingCount(0)
      return
    }

    setSelectedAccountId(preferred.id)
    setAccount(preferred)

    const [sum, open, pending] = await Promise.all([
      api.summary(preferred.id).catch(() => null),
      api.openPositions(preferred.id).catch(() => ({ positions: [], count: 0 })),
      api.pendingOrders(preferred.id).catch(() => ({ orders: [], count: 0 })),
    ])
    const summaryAccount = sum?.account || null
    setSummary(summaryAccount)
    if (summaryAccount) {
      const nextBalance = summaryAccount.balance
      const nextEquity = summaryAccount.equity
      setAccounts((prev) =>
        prev.map((a) =>
          a.id === preferred.id
            ? {
                ...a,
                balance: nextBalance ?? a.balance,
                equity: nextEquity ?? a.equity,
                sync_state: summaryAccount.sync_state || a.sync_state,
                last_sync_at: summaryAccount.last_sync_at || a.last_sync_at,
              }
            : a,
        ),
      )
      setAccount((prev) =>
        prev && prev.id === preferred.id
          ? {
              ...prev,
              balance: nextBalance ?? prev.balance,
              equity: nextEquity ?? prev.equity,
              sync_state: summaryAccount.sync_state || prev.sync_state,
              last_sync_at: summaryAccount.last_sync_at || prev.last_sync_at,
            }
          : prev,
      )
    }
    setOpenCount(open.count ?? open.positions?.length ?? 0)
    setPendingCount(pending.count ?? pending.orders?.length ?? 0)
  }, [fixtures])

  useEffect(() => {
    let cancelled = false
    async function run() {
      setLoading(true)
      try {
        await load()
      } catch (err) {
        if (!cancelled) {
          setError(safeError(err, 'Unable to load account information. Please try again.'))
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [load])

  const refresh = useCallback(async () => {
    setRefreshing(true)
    setError('')
    try {
      await load()
    } catch (err) {
      setError(safeError(err, 'Account information is temporarily unavailable. Please try again.'))
    } finally {
      setRefreshing(false)
    }
  }, [load])

  const setAccountId = useCallback(
    (id: number) => {
      setSelectedAccountId(id)
      void (async () => {
        setRefreshing(true)
        setError('')
        try {
          if (fixtures) {
            setAccount(fixtureAccount)
            setSummary(fixtureSummary)
            setOpenCount(1)
            setPendingCount(1)
            return
          }
          const preferred = accounts.find((a) => a.id === id) || null
          if (!preferred) return
          setAccount(preferred)
          const [sum, open, pending] = await Promise.all([
            api.summary(id).catch(() => null),
            api.openPositions(id).catch(() => ({ positions: [], count: 0 })),
            api.pendingOrders(id).catch(() => ({ orders: [], count: 0 })),
          ])
          setSummary(sum?.account || null)
          setOpenCount(open.count ?? open.positions?.length ?? 0)
          setPendingCount(pending.count ?? pending.orders?.length ?? 0)
        } catch (err) {
          setError(safeError(err, 'Unable to load account information. Please try again.'))
        } finally {
          setRefreshing(false)
        }
      })()
    },
    [accounts, fixtures],
  )

  const requestDemoAccount = useCallback(async () => {
    setCreatingAccount(true)
    setError('')
    setNotice('')
    try {
      await api.createAccount(`web-${Date.now()}`)
      setNotice('Demo trading account created.')
      await load()
    } catch (err) {
      if (isDemoActivationRequired(err)) {
        setNotice(DEMO_ACTIVATION_REQUEST_NOTICE)
        return
      }
      setError(
        'Demo account activation is temporarily unavailable. Please try again later or contact support.',
      )
    } finally {
      setCreatingAccount(false)
    }
  }, [load])

  const requestLiveAccount = useCallback(
    async (tradingPassword: string, confirmTradingPassword: string): Promise<TradingAccount> => {
      setCreatingLiveAccount(true)
      setError('')
      setNotice('')
      try {
        // Visual review only (`?fixtures=1`) — never hits production Live create.
        if (fixtures) {
          await new Promise((r) => window.setTimeout(r, 1600))
          const live = fixtureLiveAccount
          fixtureLiveCreatedRef.current = true
          setAccounts([fixtureAccount, live])
          setAccount(live)
          setSummary({
            id: live.id,
            mt5_login: live.mt5Login,
            server: live.server,
            mode: live.mode,
            currency: live.currency,
            balance: live.balance,
            equity: live.equity,
            floating_profit: 0,
            status: live.status,
            sync_state: 'LIVE',
            last_sync_at: new Date().toISOString(),
          })
          setSelectedAccountId(live.id)
          setNotice('Live trading account created. Opening balance is 0 USD — deposit to fund trading.')
          return live
        }
        const res = await api.createLiveAccount(
          `web-live-${Date.now()}`,
          tradingPassword,
          confirmTradingPassword,
        )
        setNotice('Live trading account created. Opening balance is 0 USD — deposit to fund trading.')
        await load()
        if (!res.account) {
          throw new Error('Live account was created but account details were not returned.')
        }
        return res.account
      } catch (err) {
        if (isLiveAlreadyExists(err)) {
          setError('A Live trading account already exists for this client.')
          await load()
          throw err
        }
        if (isLiveActivationRequired(err)) {
          setError(
            'Live account creation is temporarily unavailable. Please try again later or contact support.',
          )
          throw err
        }
        const msg =
          err instanceof Error && err.message
            ? err.message
            : 'Unable to create Live account. Please try again later.'
        setError(msg)
        throw err instanceof Error ? err : new Error(msg)
      } finally {
        setCreatingLiveAccount(false)
      }
    },
    [load, fixtures],
  )

  const mode = String(summary?.mode ?? account?.mode ?? 'DEMO').toUpperCase()
  const currency = summary?.currency || account?.currency || 'USD'
  const balance = summary?.balance ?? account?.balance
  const equity = summary?.equity ?? account?.equity
  const floating = Number(summary?.floating_profit ?? account?.floating_profit ?? 0)
  const mt5Login = summary?.mt5_login ?? account?.mt5Login
  const server = tradingServerLabel(mode, summary?.server ?? account?.server)
  const statusRaw = String(summary?.status ?? account?.status ?? '').toUpperCase()
  const isAccountActive = !statusRaw || statusRaw === 'ACTIVE'
  const status = statusLabel(summary?.status ?? account?.status)
  const syncState = summary?.sync_state || account?.sync_state || ''
  const conn = connectionStatus(syncState, { refreshing })
  const connLabel =
    conn.key === 'connected'
      ? 'MT5 Connected'
      : conn.label.startsWith('MT5')
        ? conn.label
        : conn.label
  const lastSync = summary?.last_sync_at || account?.last_sync_at
  const hasAccount = Boolean(account)
  const hasLiveAccount = accounts.some((a) => {
    const m = String(a.mode || '').toUpperCase()
    return m === 'LIVE' || m === 'REAL'
  })
  const hasDemoOnly = hasAccount && !hasLiveAccount

  const value = useMemo<WorkspaceAccountState>(
    () => ({
      loading,
      refreshing,
      error,
      notice,
      account,
      summary,
      accounts,
      openCount,
      pendingCount,
      hasAccount,
      hasLiveAccount,
      hasDemoOnly,
      mode,
      currency,
      balance,
      equity,
      floating,
      mt5Login,
      server,
      status,
      statusRaw,
      isAccountActive,
      conn,
      connLabel,
      lastSync,
      refresh,
      requestDemoAccount,
      requestLiveAccount,
      creatingAccount,
      creatingLiveAccount,
      setAccountId,
      selectedAccountId,
    }),
    [
      loading,
      refreshing,
      error,
      notice,
      account,
      summary,
      accounts,
      openCount,
      pendingCount,
      hasAccount,
      hasLiveAccount,
      hasDemoOnly,
      mode,
      currency,
      balance,
      equity,
      floating,
      mt5Login,
      server,
      status,
      statusRaw,
      isAccountActive,
      conn,
      connLabel,
      lastSync,
      refresh,
      requestDemoAccount,
      requestLiveAccount,
      creatingAccount,
      creatingLiveAccount,
      setAccountId,
      selectedAccountId,
    ],
  )

  return (
    <WorkspaceAccountContext.Provider value={value}>{children}</WorkspaceAccountContext.Provider>
  )
}

export function useWorkspaceAccount(): WorkspaceAccountState {
  const ctx = useContext(WorkspaceAccountContext)
  if (!ctx) {
    throw new Error('useWorkspaceAccount must be used within WorkspaceAccountProvider')
  }
  return ctx
}
